import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLeading } from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { appConfig } from "../../appConfig";
import { axiosInstance } from "../../axios";
import { enterprisesActions } from "./slices";
import {
  AcceptGroupInvitationParams,
  AcceptOrganizationInvitationParams,
  ChangeOrganizationOwnerParams,
  CreateGroupParams,
  CreateGroupResponse,
  CreateOrganizationParams,
  CreateOrganizationResponse,
  DeleteGroupInvitationParams,
  DeleteGroupParams,
  DeleteOrganizationInvitationParams,
  DeleteOrganizationParams,
  GetGroupInvitationParams,
  GetGroupInvitationsParams,
  GetGroupInvitationsResponse,
  GetGroupParams,
  GetGroupResponse,
  GetGroupsParams,
  GetGroupsResponse,
  GetGroupUsersParams,
  GetGroupUsersResponse,
  GetOrganizationInvitationParams,
  GetOrganizationInvitationResponse,
  GetOrganizationInvitationsParams,
  GetOrganizationInvitationsResponse,
  GetOrganizationParams,
  GetOrganizationResponse,
  GetOrganizationsParams,
  GetOrganizationsResponse,
  GetOrganizationUsersParams,
  GetOrganizationUsersResponse,
  INVITATION_TYPES,
  InviteUserToGroupParams,
  InviteUserToOrganizationParams,
  InviteUserToOrganizationResponse,
  LeaveGroupParams,
  LeaveOrganizationParams,
  RegisterNewUserParams,
  RegisterNewUserResponse,
  RemoveUserFromGroupParams,
  RemoveUserFromOrganizationParams,
  SubmitHubSpotFormParams,
  UpdateBillingAdminsListParams,
  UpdateGroupParams,
  UpdateGroupResponse,
  UpdateOrganizationParams,
  UpdateOrganizationResponse
} from "./types";

const serviceApiPath = `gotham-enterprises/method/`;

export function* getOrganizationSaga(
  action: PayloadAction<GetOrganizationParams>
): SagaIterator<void> {
  try {
    const response: AxiosResponse<GetOrganizationResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}orgs/${action.payload.id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(enterprisesActions.getOrganizationSuccess(response.data));
    }
  } catch (e) {
    yield put(
      enterprisesActions.getOrganizationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get organization data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getOrganizationsSaga(
  action: PayloadAction<GetOrganizationsParams>
): SagaIterator<void> {
  try {
    const response: AxiosResponse<GetOrganizationsResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}orgs`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(enterprisesActions.getOrganizationsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      enterprisesActions.getOrganizationsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get organizations data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createOrganizationSaga(
  action: PayloadAction<CreateOrganizationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the organization...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const response: AxiosResponse<CreateOrganizationResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}orgs/${action.payload.data.name}`
    );
    yield put(enterprisesActions.createOrganizationSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Organization has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.createOrganizationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create organization",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateOrganizationSaga(
  action: PayloadAction<UpdateOrganizationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the organization...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const response: AxiosResponse<UpdateOrganizationResponse> = yield call(
      axiosInstance.put,
      `${serviceApiPath}orgs/${action.payload.id}/${action.payload.data.name}`
    );
    yield put(enterprisesActions.updateOrganizationSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Organization has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.updateOrganizationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update organization",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteOrganizationSaga(
  action: PayloadAction<DeleteOrganizationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the organization...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    yield call(
      axiosInstance.delete,
      `${serviceApiPath}orgs/${action.payload.id}`
    );
    yield put(enterprisesActions.deleteOrganizationSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Organization has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.deleteOrganizationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete organization",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* leaveOrganizationSaga(
  action: PayloadAction<LeaveOrganizationParams>
): SagaIterator<void> {
  try {
    const { organizationId } = action.payload;
    yield put(
      notificationsActions.showNotification({
        title: "Leaving the organization...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    yield call(
      axiosInstance.post,
      `${serviceApiPath}orgs/${organizationId}/leave`
    );
    yield put(enterprisesActions.leaveOrganizationSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "You have successfully left the organization.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.leaveOrganizationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to leave organization",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getGroupSaga(
  action: PayloadAction<GetGroupParams>
): SagaIterator<void> {
  try {
    const { organizationId, groupId } = action.payload;
    const response: AxiosResponse<GetGroupResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}orgs/${organizationId}/groups/${groupId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(enterprisesActions.getGroupSuccess(response.data));
    }
  } catch (e) {
    yield put(
      enterprisesActions.getGroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get group",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getGroupsSaga(
  action: PayloadAction<GetGroupsParams>
): SagaIterator<void> {
  try {
    const response: AxiosResponse<GetGroupsResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}orgs/${action.payload.organizationId}/groups`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(enterprisesActions.getGroupsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      enterprisesActions.getGroupsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get groups",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createGroupSaga(
  action: PayloadAction<CreateGroupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the group...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, data } = action.payload;
    const response: AxiosResponse<CreateGroupResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}orgs/${organizationId}/groups/${data.name}`
    );
    yield put(enterprisesActions.createGroupSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Group has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.createGroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create group",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateGroupSaga(
  action: PayloadAction<UpdateGroupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the group...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, id, data } = action.payload;
    const response: AxiosResponse<UpdateGroupResponse> = yield call(
      axiosInstance.put,
      `${serviceApiPath}orgs/${organizationId}/groups/${id}`,
      data
    );
    yield put(enterprisesActions.updateGroupSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Group has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.updateGroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update group",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteGroupSaga(
  action: PayloadAction<DeleteGroupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the group...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, id } = action.payload;
    yield call(
      axiosInstance.delete,
      `${serviceApiPath}orgs/${organizationId}/groups/${id}`
    );
    yield put(enterprisesActions.deleteGroupSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Group has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.deleteGroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete group",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* leaveGroupSaga(
  action: PayloadAction<LeaveGroupParams>
): SagaIterator<void> {
  try {
    const { organizationId, id } = action.payload;
    yield put(
      notificationsActions.showNotification({
        title: "Leaving the group...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    yield call(
      axiosInstance.post,
      `${serviceApiPath}orgs/${organizationId}/groups/${id}/leave`
    );
    yield put(enterprisesActions.leaveGroupSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "You have successfully left the group.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.leaveGroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to leave group",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getOrganizationUsersSaga(
  action: PayloadAction<GetOrganizationUsersParams>
): SagaIterator<void> {
  try {
    const response: AxiosResponse<GetOrganizationUsersResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}orgs/${action.payload.organizationId}/users`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(enterprisesActions.getOrganizationUsersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      enterprisesActions.getOrganizationUsersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get organization users",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getGroupUsersSaga(
  action: PayloadAction<GetGroupUsersParams>
): SagaIterator<void> {
  try {
    const { organizationId, groupId } = action.payload;
    const response: AxiosResponse<GetGroupUsersResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}orgs/${organizationId}/groups/${groupId}/users`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(enterprisesActions.getGroupUsersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      enterprisesActions.getGroupUsersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get group users",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* removeUserFromOrganizationSaga(
  action: PayloadAction<RemoveUserFromOrganizationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Removing user from the organization administrators...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, userId } = action.payload;
    yield call(
      axiosInstance.delete,
      `${serviceApiPath}orgs/${organizationId}/users/${userId}`
    );
    yield put(enterprisesActions.removeUserFromOrganizationSuccess());
    yield put(
      notificationsActions.showNotification({
        title:
          "User has been successfully removed from the organization administrators.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.removeUserFromOrganizationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to remove user from the organization",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* removeUserFromGroupSaga(
  action: PayloadAction<RemoveUserFromGroupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Removing user from the group...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, groupId, userId } = action.payload;
    yield call(
      axiosInstance.delete,
      `${serviceApiPath}orgs/${organizationId}/groups/${groupId}/users/${userId}`
    );
    yield put(enterprisesActions.removeUserFromGroupSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "User has been successfully removed from the group.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.removeUserFromGroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to remove user from the group",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* submitHubSpotFormSaga(
  action: PayloadAction<SubmitHubSpotFormParams>
): SagaIterator<void> {
  try {
    yield call(axiosInstance.post, `${serviceApiPath}users/submit-form`, null, {
      params: {
        hutk: action.payload.hutk
      }
    });
    yield put(enterprisesActions.submitHubSpotFormSuccess());
  } catch (e) {
    yield put(
      enterprisesActions.submitHubSpotFormFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
  }
}

export function* getOrganizationInvitationsSaga(
  action: PayloadAction<GetOrganizationInvitationsParams>
): SagaIterator<void> {
  try {
    const response: AxiosResponse<GetOrganizationInvitationsResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}orgs/${action.payload.organizationId}/invitations`
      );
    yield put(
      enterprisesActions.getOrganizationInvitationsSuccess(response.data)
    );
  } catch (e) {
    yield put(
      enterprisesActions.getOrganizationInvitationsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get organization invitations",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getOrganizationInvitationSaga(
  action: PayloadAction<GetOrganizationInvitationParams>
): SagaIterator<void> {
  try {
    const { organizationId, invitationId } = action.payload;
    const response: AxiosResponse<GetOrganizationInvitationResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}orgs/${organizationId}/invitations/${invitationId}`
      );
    yield put(
      enterprisesActions.getOrganizationInvitationSuccess(response.data)
    );
  } catch (e) {
    yield put(
      enterprisesActions.getOrganizationInvitationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get organization invitation",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* inviteUserToOrganizationSaga(
  action: PayloadAction<InviteUserToOrganizationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending the invitation...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, email } = action.payload;
    const response: AxiosResponse<InviteUserToOrganizationResponse> =
      yield call(
        axiosInstance.post,
        `${serviceApiPath}orgs/${organizationId}/invitations`,
        {
          email,
          type: INVITATION_TYPES.ORGANIZATION_ADMINISTRATOR,
          domain: appConfig.domain
        }
      );
    yield put(
      enterprisesActions.inviteUserToOrganizationSuccess(response.data)
    );
    yield put(
      notificationsActions.showNotification({
        title: "Invitation has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.inviteUserToOrganizationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send the invitation",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* acceptOrganizationInvitationSaga(
  action: PayloadAction<AcceptOrganizationInvitationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Accepting the invitation...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, invitationId } = action.payload;
    yield call(
      axiosInstance.post,
      `${serviceApiPath}orgs/${organizationId}/invitations/${invitationId}`
    );
    yield put(enterprisesActions.acceptOrganizationInvitationSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Invitation has been successfully accepted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.acceptOrganizationInvitationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to accept the invitation",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteOrganizationInvitationSaga(
  action: PayloadAction<DeleteOrganizationInvitationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the invitation...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, invitationId } = action.payload;
    yield call(
      axiosInstance.delete,
      `${serviceApiPath}orgs/${organizationId}/invitations/${invitationId}`
    );
    yield put(enterprisesActions.deleteOrganizationInvitationSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Invitation has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.deleteOrganizationInvitationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete the invitation",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getGroupInvitationsSaga(
  action: PayloadAction<GetGroupInvitationsParams>
): SagaIterator<void> {
  try {
    const { organizationId, groupId } = action.payload;
    const response: AxiosResponse<GetGroupInvitationsResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}orgs/${organizationId}/groups/${groupId}/invitations`
    );
    yield put(enterprisesActions.getGroupInvitationsSuccess(response.data));
  } catch (e) {
    yield put(
      enterprisesActions.getGroupInvitationsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get group invitations",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getGroupInvitationSaga(
  action: PayloadAction<GetGroupInvitationParams>
): SagaIterator<void> {
  try {
    const { organizationId, groupId, invitationId } = action.payload;
    const response: AxiosResponse<GetOrganizationInvitationResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}orgs/${organizationId}/groups/${groupId}/invitations/${invitationId}`
      );
    yield put(enterprisesActions.getGroupInvitationSuccess(response.data));
  } catch (e) {
    yield put(
      enterprisesActions.getGroupInvitationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get group invitation",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* inviteUserToGroupSaga(
  action: PayloadAction<InviteUserToGroupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending the invitation...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, groupId, email } = action.payload;
    const response: AxiosResponse<InviteUserToOrganizationResponse> =
      yield call(
        axiosInstance.post,
        `${serviceApiPath}orgs/${organizationId}/groups/${groupId}/invitations`,
        {
          email,
          type: INVITATION_TYPES.GROUP_MEMBER,
          domain: appConfig.domain
        }
      );
    yield put(enterprisesActions.inviteUserToGroupSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Invitation has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.inviteUserToGroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send the invitation",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* acceptGroupInvitationSaga(
  action: PayloadAction<AcceptGroupInvitationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Accepting the invitation...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, groupId, invitationId } = action.payload;
    yield call(
      axiosInstance.post,
      `${serviceApiPath}orgs/${organizationId}/groups/${groupId}/invitations/${invitationId}`
    );
    yield put(enterprisesActions.acceptGroupInvitationSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Invitation has been successfully accepted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.acceptGroupInvitationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to accept the invitation",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteGroupInvitationSaga(
  action: PayloadAction<DeleteGroupInvitationParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the invitation...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, groupId, invitationId } = action.payload;
    yield call(
      axiosInstance.delete,
      `${serviceApiPath}orgs/${organizationId}/groups/${groupId}/invitations/${invitationId}`
    );
    yield put(enterprisesActions.deleteGroupInvitationSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Invitation has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.deleteGroupInvitationFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete the invitation",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* changeOrganizationOwnerSaga(
  action: PayloadAction<ChangeOrganizationOwnerParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending an invitation to become the organization owner...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { organizationId, email } = action.payload;
    yield call(
      axiosInstance.post,
      `${serviceApiPath}orgs/${organizationId}/invitations`,
      {
        email,
        type: INVITATION_TYPES.ORGANIZATION_OWNER,
        domain: appConfig.domain
      }
    );
    yield put(enterprisesActions.changeOrganizationOwnerSuccess());
    yield put(
      notificationsActions.showNotification({
        title:
          "The invitation to become the organization owner has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.changeOrganizationOwnerFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send invitation to become the organization owner.",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* registerNewUserUserSaga(
  action: PayloadAction<RegisterNewUserParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Registering new user...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { data } = action.payload;
    const response: AxiosResponse<RegisterNewUserResponse> = yield call(
      axiosInstance.post,
      `${serviceApiPath}users`,
      data
    );
    yield put(enterprisesActions.registerNewUserSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "User has been successfully registered",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.registerNewUserFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to register user",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateBillingAdminsListSaga(
  action: PayloadAction<UpdateBillingAdminsListParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the organization billing admins list...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { orgId, admins } = action.payload;
    yield call(axiosInstance.put, `${serviceApiPath}orgs/${orgId}`, admins);
    yield put(enterprisesActions.updateBillingAdminsListSuccess());
    yield put(
      notificationsActions.showNotification({
        title:
          "Organization billing admins list has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      enterprisesActions.updateBillingAdminsListFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update organization billing admins list",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(enterprisesActions.getOrganizationRequest, getOrganizationSaga),
    takeLeading(
      enterprisesActions.getOrganizationsRequest,
      getOrganizationsSaga
    ),
    takeEvery(
      enterprisesActions.createOrganizationRequest,
      createOrganizationSaga
    ),
    takeEvery(
      enterprisesActions.updateOrganizationRequest,
      updateOrganizationSaga
    ),
    takeEvery(
      enterprisesActions.deleteOrganizationRequest,
      deleteOrganizationSaga
    ),
    takeEvery(
      enterprisesActions.leaveOrganizationRequest,
      leaveOrganizationSaga
    ),
    takeLeading(enterprisesActions.getGroupRequest, getGroupSaga),
    takeLeading(enterprisesActions.getGroupsRequest, getGroupsSaga),
    takeEvery(enterprisesActions.createGroupRequest, createGroupSaga),
    takeEvery(enterprisesActions.updateGroupRequest, updateGroupSaga),
    takeEvery(enterprisesActions.deleteGroupRequest, deleteGroupSaga),
    takeEvery(enterprisesActions.leaveGroupRequest, leaveGroupSaga),
    takeLeading(
      enterprisesActions.getOrganizationUsersRequest,
      getOrganizationUsersSaga
    ),
    takeLeading(enterprisesActions.getGroupUsersRequest, getGroupUsersSaga),
    takeEvery(
      enterprisesActions.removeUserFromGroupRequest,
      removeUserFromGroupSaga
    ),
    takeEvery(
      enterprisesActions.removeUserFromOrganizationRequest,
      removeUserFromOrganizationSaga
    ),
    takeEvery(
      enterprisesActions.submitHubSpotFormRequest,
      submitHubSpotFormSaga
    ),
    takeLeading(
      enterprisesActions.getOrganizationInvitationsRequest,
      getOrganizationInvitationsSaga
    ),
    takeLeading(
      enterprisesActions.getOrganizationInvitationRequest,
      getOrganizationInvitationSaga
    ),
    takeEvery(
      enterprisesActions.inviteUserToOrganizationRequest,
      inviteUserToOrganizationSaga
    ),
    takeEvery(
      enterprisesActions.acceptOrganizationInvitationRequest,
      acceptOrganizationInvitationSaga
    ),
    takeEvery(
      enterprisesActions.deleteOrganizationInvitationRequest,
      deleteOrganizationInvitationSaga
    ),
    takeEvery(
      enterprisesActions.changeOrganizationOwnerRequest,
      changeOrganizationOwnerSaga
    ),
    takeLeading(
      enterprisesActions.getGroupInvitationsRequest,
      getGroupInvitationsSaga
    ),
    takeLeading(
      enterprisesActions.getGroupInvitationRequest,
      getGroupInvitationSaga
    ),
    takeEvery(
      enterprisesActions.inviteUserToGroupRequest,
      inviteUserToGroupSaga
    ),
    takeEvery(
      enterprisesActions.acceptGroupInvitationRequest,
      acceptGroupInvitationSaga
    ),
    takeEvery(
      enterprisesActions.deleteGroupInvitationRequest,
      deleteGroupInvitationSaga
    ),
    takeEvery(
      enterprisesActions.registerNewUserRequest,
      registerNewUserUserSaga
    ),
    takeEvery(
      enterprisesActions.updateBillingAdminsListRequest,
      updateBillingAdminsListSaga
    )
  ]);
}
